import React, { useState, useEffect } from 'react';
import ColorPaletteTester from '../components/ColorPaletteTester';
import ColorDebugger from '../components/ColorDebugger';
import HeaderBanner from '../components/HeaderBanner';
import PaletteSwitcher from '../components/PaletteSwitcher';

const ColorPaletteTestPage: React.FC = () => {
  const [currentPalette, setCurrentPalette] = useState('nature-forest');
  const [isLoading, setIsLoading] = useState(false);

  // Load palette CSS dynamically
  const loadPaletteCSS = (paletteName: string) => {
    return new Promise<void>((resolve, reject) => {
      // Remove existing palette CSS
      const existingLink = document.getElementById('palette-css');
      if (existingLink) {
        existingLink.remove();
      }

      // Create new link element
      const link = document.createElement('link');
      link.id = 'palette-css';
      link.rel = 'stylesheet';
      link.href = `/src/styles/palettes/${paletteName}.css`;
      
      link.onload = () => {
        console.log(`✅ Palette '${paletteName}' loaded successfully`);
        resolve();
      };
      
      link.onerror = () => {
        console.error(`❌ Failed to load palette '${paletteName}'`);
        reject(new Error(`Failed to load palette: ${paletteName}`));
      };

      // Add to head
      document.head.appendChild(link);
    });
  };

  const handlePaletteChange = async (paletteName: string) => {
    if (paletteName === currentPalette) return;
    
    setIsLoading(true);
    
    try {
      // Load the new palette CSS
      await loadPaletteCSS(paletteName);
      
      // Call the API (for logging/tracking purposes)
      const response = await fetch(`/api/apply-palette?palette=${paletteName}`);
      if (response.ok) {
        const data = await response.json();
        console.log('API Response:', data);
      }
      
      // Update state
      setCurrentPalette(paletteName);
      
      // Show success message
      console.log(`🎨 Switched to palette: ${paletteName}`);
      
    } catch (error) {
      console.error('Error applying palette:', error);
      // Show error but don't revert - user can try again
      alert(`Failed to load palette '${paletteName}'. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial palette on component mount
  useEffect(() => {
    loadPaletteCSS(currentPalette).catch(console.error);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderBanner />
      <div className="container mx-auto py-8 space-y-8">
        <PaletteSwitcher 
          currentPalette={currentPalette}
          onPaletteChange={handlePaletteChange}
          isLoading={isLoading}
        />
        <ColorDebugger />
        <ColorPaletteTester 
          paletteName={`NLFB Color Palette Test - ${currentPalette.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}`}
          className="bg-white rounded-lg shadow-lg"
        />
      </div>
    </div>
  );
};

export default ColorPaletteTestPage; 